import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from './modal';

/**
 * Convenience wrapper that owns the open/close state for a Modal and renders
 * a trigger element next to it.
 *
 * Usage with a function trigger (preferred):
 *
 *   <ModalTrigger
 *     trigger={ ({ open }) => (
 *       <button onClick={ open }>Edit Payment</button>
 *     ) }
 *     size="lg"
 *     title="Manage Payment"
 *   >
 *     { ({ close }) => <CardForm onDone={ close } /> }
 *   </ModalTrigger>
 *
 * Usage with an element trigger:
 *
 *   <ModalTrigger
 *     trigger={ <button>Open</button> }
 *     title="Hello"
 *   >
 *     <p>Body</p>
 *   </ModalTrigger>
 *
 * The render-prop body receives `{ close }` so the modal contents can dismiss
 * themselves on a successful action without needing to lift state.
 */
const ModalTrigger = ({ trigger, children, ...modalProps }) => {
	const [ isOpen, setIsOpen ] = useState(false);
	const open  = useCallback(() => setIsOpen(true),  []);
	const close = useCallback(() => setIsOpen(false), []);

	let renderedTrigger = null;
	if (typeof trigger === 'function') {
		renderedTrigger = trigger({ open, close, isOpen });
	} else if (React.isValidElement(trigger)) {
		const existingOnClick = trigger.props && trigger.props.onClick;
		renderedTrigger = React.cloneElement(trigger, {
			onClick: (e) => {
				if (existingOnClick) existingOnClick(e);
				if (!e.defaultPrevented) open();
			},
		});
	}

	return (
		<React.Fragment>
			{ renderedTrigger }
			<Modal isOpen={ isOpen } onClose={ close } { ...modalProps }>
				{ typeof children === 'function' ? children({ close }) : children }
			</Modal>
		</React.Fragment>
	);
};

ModalTrigger.propTypes = {
	trigger: PropTypes.oneOfType([ PropTypes.func, PropTypes.element ]).isRequired,
	children: PropTypes.oneOfType([ PropTypes.func, PropTypes.node ]),
};

export default ModalTrigger;
